import { Integration } from "models/integration";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { SaveOrUpdateCampaignsIntegrationFunction } from "../../viewModel";
import { TextField } from "components/Forms/TextField";
import { Button } from "components/Button";
import { CopyText } from "components/Forms/CopyText";
import { useTranslation } from "react-i18next";

export type MetaIntegrationForm = {
  type: string;
  name: string;
  adsId: string;
  token: string;
};

type Props = {
  selectedIntegration: Integration | undefined;
  onSaveOrUpdate: SaveOrUpdateCampaignsIntegrationFunction;
  onClose: () => void;
};

export const MetaForm = ({
  selectedIntegration,
  onClose,
  onSaveOrUpdate,
}: Props) => {
  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm<MetaIntegrationForm>();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    register("type", {
      required: {
        value: true,
        message: t("providers.view.integrations.form.validation.invalid_type"),
      },
    });
  }, [register]);

  useEffect(() => {
    setValue("name", selectedIntegration?.name ?? "");
    setValue("type", selectedIntegration?.type ?? "facebook");
    setValue("adsId", selectedIntegration?.adsId ?? "");
    setValue("token", selectedIntegration?.token ?? "");
  }, [selectedIntegration, setValue]);

  const handleSubmitForm = async (data: MetaIntegrationForm) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      toast.info(
        selectedIntegration
          ? t("providers.view.integrations.form.update_loading")
          : t("providers.view.integrations.form.creating_loading")
      );
      await onSaveOrUpdate(data);
      toast.success(
        selectedIntegration
          ? t("providers.view.integrations.form.update_success")
          : t("providers.view.integrations.form.creating_loading")
      );
      onClose();
    } catch (error) {
      toast.error(t("providers.view.integrations.form.error"));
    }
    setIsLoading(false);
  };

  return (
    <form
      onSubmit={handleSubmit(handleSubmitForm)}
      className="flex flex-col gap-5"
    >
      <CopyText
        label={t(
          "providers.view.integrations.form.fields.token_generation_link"
        )}
        text="https://developers.facebook.com/apps/433547785692456/marketing-api/tools/?business_id=338986327442366"
        variant="secondary"
      />
      <TextField
        label={t("providers.view.integrations.form.fields.account_name")}
        placeholder={t(
          "providers.view.integrations.form.fields.account_name_placeholder"
        )}
        error={errors.name?.message}
        register={register("name", {
          required: {
            value: true,
            message: t(
              "providers.view.integrations.form.fields.account_name_required"
            ),
          },
        })}
        variant="secondary"
      />
      <TextField
        label={t("providers.view.integrations.form.fields.campaign_identifier")}
        placeholder={t(
          "providers.view.integrations.form.fields.campaign_identifier_placeholder"
        )}
        error={errors.adsId?.message}
        register={register("adsId", {
          required: {
            value: true,
            message: t(
              "providers.view.integrations.form.fields.campaign_identifier_required"
            ),
          },
        })}
        variant="secondary"
      />
      <TextField
        label={t("providers.view.integrations.form.fields.access_token")}
        placeholder={t(
          "providers.view.integrations.form.fields.access_token_placeholder"
        )}
        error={errors.token?.message}
        register={register("token", {
          required: {
            value: true,
            message: t(
              "providers.view.integrations.form.fields.access_token_required"
            ),
          },
        })}
        variant="secondary"
      />
      <div className="modalButtons">
        <Button onClick={onClose} theme="cancel">
          {t("actions.cancel")}
        </Button>
        <Button loading={isLoading} theme="highlight">
          {selectedIntegration ? t("actions.edit") : t("actions.add")}
        </Button>
      </div>
    </form>
  );
};
